import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  checkExistingAuth,
  redirectToLogin,
  signOutForPublicSignup,
} from "common/Auth/cognitoAuth";
import {
  clearSignupInviteReturnUrl,
  getPublicSignupSignOutUrl,
} from "common/Auth/publicAuthPaths";
import { setAuthToken } from "common/HttpClient";
import { setAuthTokenPAdmin } from "common/HttpClient/http-client-pAdmin";
import { stopTokenRefresh } from "common/HttpClient/tokenManager";
import { SIGNUP_UI } from "./signup.constants";
import {
  buildInviteAcceptancePayload,
  formatPhoneNumberE164,
  parseInviteQueryParams,
  validatePhoneNumber,
  validateSignupForm,
} from "./signup.validation";
import {
  checkIdentifierExists,
  postInviteAcceptance,
  validateInviteToken,
} from "./Signup.services";

const INITIAL_FORM_VALUES = {
  email: "",
  firstName: "",
  lastName: "",
  phoneNumber: "",
  password: "",
  confirmPassword: "",
  acceptedTerms: false,
};

const validatePhoneAvailability = async (phoneNumber) => {
  const formatError = validatePhoneNumber(phoneNumber);
  if (formatError) {
    return { error: formatError };
  }

  const e164 = formatPhoneNumberE164(phoneNumber);
  if (!e164) {
    return { error: validatePhoneNumber(phoneNumber) };
  }

  try {
    const result = await checkIdentifierExists({ phoneNumber: e164 });
    if (result?.error) {
      return { error: SIGNUP_UI.phoneValidateFailedError };
    }
    if (result?.exists) {
      return { error: SIGNUP_UI.phoneAlreadyExistsError };
    }
    return { error: null };
  } catch {
    return { error: SIGNUP_UI.phoneValidateFailedError };
  }
};

const clearFieldErrorIfMatches = (prev, field, messages) => {
  if (!messages.includes(prev[field])) {
    return prev;
  }
  const next = { ...prev };
  delete next[field];
  return next;
};

export function useSignup() {
  const location = useLocation();
  const invite = useMemo(
    () => parseInviteQueryParams(location.search),
    [location.search]
  );

  const [tokenValidation, setTokenValidation] = useState({
    status: "idle",
    email: "",
    requiresSignup: false,
  });
  const [formValues, setFormValues] = useState(INITIAL_FORM_VALUES);
  const [errors, setErrors] = useState({});
  const [bannerError, setBannerError] = useState("");
  const [loading, setLoading] = useState(false);
  const [availabilityChecking, setAvailabilityChecking] = useState(false);
  const [success, setSuccess] = useState(false);
  const [sessionCheck, setSessionCheck] = useState("loading");
  const [logoutLoading, setLogoutLoading] = useState(false);

  useEffect(() => {
    if (invite.isValid) {
      clearSignupInviteReturnUrl();
    }
  }, [invite.isValid]);

  useEffect(() => {
    let cancelled = false;

    checkExistingAuth()
      .then((profile) => {
        if (cancelled) return;
        setSessionCheck(profile ? "active" : "clear");
      })
      .catch(() => {
        if (!cancelled) {
          setSessionCheck("clear");
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const handleLogoutForSignup = useCallback(async () => {
    setLogoutLoading(true);
    try {
      stopTokenRefresh();
      setAuthToken(null);
      setAuthTokenPAdmin(null);
      const { redirected } = await signOutForPublicSignup(
        getPublicSignupSignOutUrl()
      );
      if (redirected) {
        return;
      }
      setTokenValidation({
        status: "loading",
        email: "",
        requiresSignup: false,
      });
      setSessionCheck("clear");
    } finally {
      setLogoutLoading(false);
    }
  }, []);

  useEffect(() => {
    if (sessionCheck !== "clear") {
      return;
    }

    if (!invite.isValid) {
      setTokenValidation({
        status: "error",
        email: "",
        requiresSignup: false,
      });
      return;
    }

    let cancelled = false;
    setTokenValidation({
      status: "loading",
      email: "",
      requiresSignup: false,
    });

    validateInviteToken(invite.inviteToken)
      .then((result) => {
        if (cancelled) return;
        if (result?.error || !result?.email) {
          setTokenValidation({
            status: "error",
            email: "",
            requiresSignup: false,
          });
          return;
        }
        setTokenValidation({
          status: "ready",
          email: result.email,
          requiresSignup: result.requiresSignup === true,
        });
        setFormValues((prev) => ({
          ...prev,
          email: result.email,
        }));
      })
      .catch(() => {
        if (cancelled) return;
        setTokenValidation({
          status: "error",
          email: "",
          requiresSignup: false,
        });
      });

    return () => {
      cancelled = true;
    };
  }, [invite.inviteToken, invite.isValid, sessionCheck]);

  const inviteForSubmit = useMemo(
    () => ({
      inviteToken: invite.inviteToken,
      email: tokenValidation.email,
    }),
    [invite.inviteToken, tokenValidation.email]
  );

  const canShowSignupForm =
    invite.isValid &&
    tokenValidation.status === "ready" &&
    tokenValidation.requiresSignup;

  const handleChange = useCallback((name, value) => {
    setFormValues((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => {
      if (!prev[name]) return prev;
      const next = { ...prev };
      delete next[name];
      return next;
    });
    setBannerError("");
  }, []);

  const handlePhoneBlur = useCallback(async () => {
    const phoneResult = await validatePhoneAvailability(formValues.phoneNumber);
    if (phoneResult.error) {
      setErrors((prev) => ({ ...prev, phoneNumber: phoneResult.error }));
      return;
    }
    setErrors((prev) =>
      clearFieldErrorIfMatches(prev, "phoneNumber", [
        SIGNUP_UI.phoneAlreadyExistsError,
        SIGNUP_UI.phoneValidateFailedError,
      ])
    );
  }, [formValues.phoneNumber]);

  const handleFieldBlur = useCallback(
    (name) => {
      if (name !== "phoneNumber") {
        return Promise.resolve();
      }
      setAvailabilityChecking(true);
      return handlePhoneBlur().finally(() => setAvailabilityChecking(false));
    },
    [handlePhoneBlur]
  );

  const submit = useCallback(
    async (event) => {
      event?.preventDefault?.();
      setBannerError("");
      setErrors({});

      if (!canShowSignupForm) {
        setBannerError(SIGNUP_UI.inviteExpiredMessage);
        return;
      }

      const { errors: validationErrors, isValid } = validateSignupForm(formValues);
      if (!isValid) {
        setErrors(validationErrors);
        return;
      }

      setLoading(true);
      try {
        const phoneResult = await validatePhoneAvailability(formValues.phoneNumber);
        if (phoneResult.error) {
          setErrors({ phoneNumber: phoneResult.error });
          return;
        }

        const payload = buildInviteAcceptancePayload(inviteForSubmit, formValues);
        const response = await postInviteAcceptance(payload);
        if (response?.error) {
          setBannerError(response.error || SIGNUP_UI.submitErrorMessage);
          return;
        }
        setSuccess(true);
        setTimeout(async () => {
          await redirectToLogin(() => {}, () => {});
        }, 2500);
      } catch {
        setBannerError(SIGNUP_UI.submitErrorMessage);
      } finally {
        setLoading(false);
      }
    },
    [canShowSignupForm, formValues, inviteForSubmit]
  );

  return {
    invite,
    sessionCheck,
    logoutLoading,
    handleLogoutForSignup,
    tokenValidation,
    canShowSignupForm,
    formValues,
    errors,
    bannerError,
    loading,
    availabilityChecking,
    success,
    handleChange,
    handleFieldBlur,
    submit,
  };
}
